import React from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, Users, IndianRupee, Utensils, Rocket, ShieldCheck } from "lucide-react";
import { imgUrl } from "@/lib/api";

export default function HostelCard({ h }) {
  const mealsIncluded = h.meal_available && (!h.meal_price || h.meal_price === 0);
  return (
    <Link
      to={`/hostels/${h.id}`}
      data-testid={`hostel-card-${h.id}`}
      className={`card-lift group block bg-white border rounded-3xl overflow-hidden shadow-md hover:border-vermilion-600 ${h.is_featured ? "border-turmeric-400 ring-2 ring-turmeric-200/60" : "border-sandstone-200"}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        <img
          src={imgUrl(h.images?.[0])}
          alt={h.name}
          loading="lazy"
          decoding="async"
          width="800"
          height="600"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
        />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/35 to-transparent pointer-events-none" aria-hidden />
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          {h.is_featured && (
            <span
              data-testid={`hostel-featured-badge-${h.id}`}
              className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-black px-2 py-1 rounded-full bg-turmeric-400 text-jodhpur-900 shadow-sm"
              title="Sponsored — this owner paid for premium visibility"
            >
              <Rocket size={11} /> Sponsored
            </span>
          )}
          <span className={`text-[10px] tracking-widest uppercase font-bold px-2 py-1 rounded-full ${h.gender === "girls" ? "bg-bandhani-400/90 text-white" : h.gender === "boys" ? "bg-jodhpur-600/90 text-white" : "bg-peacock-600/90 text-white"}`}>
            {h.gender}
          </span>
        </div>
        <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur rounded-full px-2.5 py-1 text-xs flex items-center gap-1 font-semibold">
          <Star size={12} className="fill-turmeric-500 text-turmeric-500" /> {h.rating}
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-lg leading-tight">{h.name}</h3>
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <MapPin size={12} /> {h.city}{h.coaching_centers?.[0] ? ` · near ${h.coaching_centers[0]}` : ""}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="flex items-baseline text-vermilion-600 font-display font-black text-xl">
              <IndianRupee size={14} strokeWidth={3} />{h.price_per_bed}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">/ bed / night</div>
            <div
              data-testid={`hostel-monthly-price-${h.id}`}
              className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-peacock-500 text-white"
              title="Monthly Saver: 10% off on stays of 30+ nights"
            >
              ₹{Math.round(h.price_per_bed * 30 * 0.9).toLocaleString("en-IN")}/mo
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <span
            data-testid={`no-fees-badge-${h.id}`}
            title="Zero platform fees — you pay exactly the listed price"
            className="text-[10px] tracking-wider uppercase font-bold px-2 py-1 rounded-md bg-emerald-100 text-emerald-800 flex items-center gap-1"
          >
            <ShieldCheck size={10} /> No hidden charges
          </span>
          {mealsIncluded && (
            <span className="text-[10px] tracking-wider uppercase font-bold px-2 py-1 rounded-md bg-turmeric-400 text-jodhpur-900 flex items-center gap-1">
              <Utensils size={10} /> Lunch + dinner free
            </span>
          )}
          {(h.amenities || []).slice(0, 3).map((a) => (
            <span key={a} className="text-[10px] tracking-wider uppercase font-semibold px-2 py-1 rounded-md bg-sandstone-100 text-stone-700">{a}</span>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="flex items-center gap-1 text-peacock-600 font-medium"><Users size={12} /> {h.available_beds} beds free</span>
          <span className="text-slate-500">{h.coaching_centers?.[0] && `near ${h.coaching_centers[0]}`}</span>
        </div>
      </div>
    </Link>
  );
}
